/**
 * D-3 会话历史 —— 契约 §7 D-3 / conversation 的 `ConversationDtos.java`。
 *
 * ★ 这一组端点补的缺口是：**刷新页面之后还能看到刚才聊了什么**。
 *   W1-4 的本地落盘（`lib/chat-storage.ts`）只兜住「同一台设备、同一程对话」，
 *   换设备、换浏览器、清了缓存，服务端记着而界面记不得 —— 那才是这一组要解决的。
 *
 * ⚠ **形状照后端 record 抄，不照契约文字**（W3 的教训：契约文字与实现有出入时，
 *   跑着的代码才是真相源）。
 *
 * ⚠⚠ **两种分页刻意不同，别统一**：
 *   会话**列表**用 `page/size`（增长慢，offset 没问题）；
 *   会话**消息**用游标 `before/limit`（消息持续追加，offset 会让第 2 页与第 1 页重叠或漏条）。
 *   后端 `ConversationMemoryService.listMessages` 的头注专门写了这条。
 */
import { z } from 'zod'
import { cardEnvelopeSchema, type CardEnvelope } from './cards'

/**
 * 历史里出现的三种角色。
 *
 * ⚠⚠ **没有 `tool`** —— 后端在 SQL 里用白名单 `.in(USER, ASSISTANT, CARD)` 挡掉了，
 *   理由是工具报文又大又常含证件号明文，而「回看历史」是最不设防的入口。
 *   所以**前端不用自己过滤 tool 行**，也别为了「保险」再滤一遍：
 *   多滤一遍会让人以为这里还可能收到 tool，下一个人就会去猜它长什么样。
 */
export const MESSAGE_ROLES = ['user', 'assistant', 'card'] as const
export type MessageRole = (typeof MESSAGE_ROLES)[number]

/**
 * 会话列表的一行（`ConversationSummaryView`）。
 *
 * ⚠⚠ `title` 是**用户第一条消息的原文**（后端按 `seq = 1` 取），
 *   含手机号、乘机人姓名这类东西 —— 后端 DTO 的注释专门点了名。
 *   **展示要截断，且永不进埋点、URL、Sentry**。见 `lib/monitoring.ts` 的隐私守门。
 * ⚠ `startedAt` / `lastMessageAt` 是 ISO-8601 **本地时间串**（`2026-09-02T14:30`），
 *   **不带时区** —— 后端 `iso()` 就是 `LocalDateTime.toString()`。
 *   与卡片里的 `depart` / `arrive` 是同一类坑：按 Asia/Shanghai 理解，
 *   直接丢给 `new Date()` 会被当成浏览器本地时区，换个时区跑就整体偏移。
 */
export const conversationSummarySchema = z.looseObject({
  conversationId: z.string().min(1),
  /** ⚠ 敏感：用户原话。可能为 null（理论上不会，但空列表这类边角上宁可容错）。 */
  title: z.string().nullable().default(null),
  startedAt: z.string().nullable().default(null),
  lastMessageAt: z.string().nullable().default(null),
  messageCount: z.number().int().nonnegative().default(0),
})
export type ConversationSummary = z.infer<typeof conversationSummarySchema>

/**
 * 一条历史消息（`MessageView`）。
 *
 * ⚠ `seq` 是**游标本体**：下一页传 `?before=<本页第一条的 seq>`。
 * ⚠⚠ `roundNo` 不是装饰 —— 它是把「分行的历史」还原成「直播时的气泡」的**唯一依据**，
 *   见 `apps/app/src/lib/history-merge.ts`。没有它就只能靠相邻关系猜，猜错就分组错。
 * ⚠⚠ `content` **由 `role` 决定怎么解释**：
 *   `user` / `assistant` 是纯文本；**`card` 是 SSE data 事件的原始 JSON 串**
 *   （形如 `{"type":"flight_options",...}`）—— 与直播时收到的**是同一份东西**，
 *   所以卡片能原样重放，不必为历史另写一套渲染。解析走 {@link parseCardContent}。
 */
export const historyMessageSchema = z.looseObject({
  seq: z.number().int(),
  /** 轮次。⚠ 用户行开新轮；同一轮的 assistant / card 行属于**同一个气泡**。 */
  roundNo: z.number().int().default(0),
  /**
   * ⚠ 用 `z.string()` 而不是 enum：后端将来放行第四种角色时，前端不该整页解析失败 ——
   *   认不出的角色由展示层跳过，这比白屏好。
   */
  role: z.string().min(1),
  content: z.string().nullable().default(null),
  /**
   * 路由到的域。⚠ 用户行可能为 null / 空串（落库时路由还没发生）。
   *
   * ⚠⚠ **这里是【小写】**：`flight` / `hotel` / `ride` —— 值来自 AI 侧的**节点名**
   *   （`ai_core/graph/build.py` 的 `HOTEL = "hotel"` …），由 `tag_domain()` 回填。
   *
   * 🔴🔴 **与 `OrderSummary.domain` 不是一回事，而且大小写相反**：
   *   那个是**大写**（`FLIGHT`，proto 枚举名去 `DOMAIN_` 前缀，经 trade 落库）。
   *   两个字段同名、同义、不同源、不同大小写 —— 把任一边的比较逻辑抄到另一边，
   *   **不报错，只是永远不命中**。⇒ 订单那边一律走 `normaliseOrderDomain()`；
   *   这边今天只用来分组展示，真要比较请单独写，别复用那个函数。
   */
  domain: z.string().nullable().default(null),
  /** ISO-8601 本地时间串，**不带时区**，同 {@link conversationSummarySchema}。 */
  createdAt: z.string().nullable().default(null),
})
export type HistoryMessage = z.infer<typeof historyMessageSchema>

/**
 * `GET /api/conversations/{id}/messages` 直接回**裸数组**（不套 `PageResult`）——
 * 游标分页没有 total、没有页码，套壳只会让人以为能翻页码。
 *
 * ⚠⚠ **恒为 `seq` 升序（旧 → 新），可直接顺序渲染**；返回**空数组 = 到头了，不是出错**。
 *   后端是倒序取 N 条再翻转的（"最近 N 条"必须从尾部取），所以别在前端再排一次。
 */
export const historyMessageListSchema = z.array(historyMessageSchema)

/**
 * 分页边界 —— 抄自后端 `ConversationMemoryService`（`DEFAULT_PAGE_SIZE` / `MAX_PAGE_SIZE`）。
 *
 * ⚠ 超过上限后端**静默截断**（`Math.min`），不会报错。前端发 500 会拿回 100 条，
 *   界面上却按 500 算「是不是到头了」—— 于是永远以为还有下一页。所以在客户端就夹住。
 */
export const CONVERSATION_PAGE_SIZE = 20
export const CONVERSATION_MAX_PAGE_SIZE = 100

/**
 * 把 `card` 行的 `content` 还原成卡片信封。
 *
 * ⚠⚠ **失败返回 `null`，由调用方渲染明确的占位** —— 这是 W4 拍下来的那条：
 *   直播那条路的降级是「退回文本」，但**历史里没有对应的文本可退**
 *   （后端有卡片时话术只剩一句概述，逐条候选不再进话术）。
 *   静默跳过的话，用户看到的是一段没头没尾的对话，**而且没有人会来报 bug** ——
 *   他只会觉得这个产品的历史记录不太靠谱。
 *
 * ⚠ 与「不认识的 `type`」是**两件事**，别合并：
 *   type 不认识走 `UnknownCard`，那是契约 §6.1 双向承诺下的**正常路径**；
 *   这里返回 null 的是 `JSON.parse` 抛了或连 `type` 都没有 —— **异常路径**。
 *   两者文案共用的话，后端新发一张卡会被误报成「坏数据」。
 */
export function parseCardContent(content: string | null | undefined): CardEnvelope | null {
  if (!content) return null
  let raw: unknown
  try {
    raw = JSON.parse(content)
  } catch {
    return null
  }
  const parsed = cardEnvelopeSchema.safeParse(raw)
  return parsed.success ? parsed.data : null
}
